import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Stack,
  TextField
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import { memo, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import { API_BASE_URL, authFetch, authFetchJson } from '../../apiClient.ts';
import { Link } from 'react-router-dom';
import type { PipelineRow } from '../../types/PipelineRow.ts';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';
import {
  compareReportDates,
  formatReportDate,
  getErrorMessage,
  parseEmailList
} from '../../utils/helperUtils.ts';

interface Props {
  isDashboard?: boolean;
  showEmailButton?: boolean;
}

interface PipelineGridProps {
  columns: GridColDef[];
  isDashboard: boolean;
  rows: PipelineRow[];
}

interface PipelineReportEmailButtonProps {
  days?: string;
  size?: 'small' | 'medium' | 'large';
}

const PipelineGrid = memo<PipelineGridProps>(({ columns, isDashboard, rows }) => (
  <Box sx={{ height: isDashboard ? 320 : 600, width: '100%' }}>
    <DataGrid
      rows={rows}
      columns={columns}
      pagination
      pageSizeOptions={isDashboard ? [10, 25, 50] : [10, 25, 50]}
      initialState={{
        pagination: { paginationModel: { pageSize: isDashboard ? 10 : 25, page: 0 } },
        sorting: {
          sortModel: [{ field: 'dateOpened', sort: 'desc' }]
        }
      }}
      density={isDashboard ? 'compact' : 'standard'}
      disableRowSelectionOnClick
    />
  </Box>
));

const getFilenameFromContentDisposition = (contentDisposition: string | null, fallback: string) => {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 60_000);
};

export const PipelineReportEmailButton = memo<PipelineReportEmailButtonProps>(
  ({ days, size = 'medium' }) => {
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [emailError, setEmailError] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const notify = useNotify();

    const handleSendEmail = async () => {
      const { emails, invalid } = parseEmailList(emailInput);
      if (invalid.length > 0) {
        setEmailError(`Invalid email(s): ${invalid.join(', ')}`);
        return;
      }

      if (emails.length === 0) {
        setEmailError('Enter at least one email address.');
        return;
      }

      setSendingEmail(true);
      setEmailError('');
      try {
        const payload: { emails: string[]; days?: string } = { emails };
        if (days) {
          payload.days = days;
        }
        await authFetchJson(`${API_BASE_URL}/reports/pipeline/email`, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        notify('Pipeline report email sent', { type: 'info' });
        setEmailDialogOpen(false);
        setEmailInput('');
      } catch (error) {
        notify(`Error: ${getErrorMessage(error)}`, { type: 'error' });
      } finally {
        setSendingEmail(false);
      }
    };

    return (
      <>
        <Button
          variant="outlined"
          startIcon={<EmailIcon />}
          onClick={() => setEmailDialogOpen(true)}
          size={size}
        >
          Email To
        </Button>
        <Dialog
          open={emailDialogOpen}
          onClose={() => {
            if (!sendingEmail) {
              setEmailDialogOpen(false);
              setEmailError('');
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Email Pipeline Report</DialogTitle>
          <DialogContent>
            <TextField
              label="Email addresses"
              placeholder="name@company.com, other@company.com"
              value={emailInput}
              onChange={(event) => {
                setEmailInput(event.target.value);
                setEmailError('');
              }}
              error={emailError.length > 0}
              helperText={
                emailError.length > 0
                  ? emailError
                  : 'Separate multiple email addresses with commas.'
              }
              fullWidth
              margin="normal"
              multiline
              minRows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (!sendingEmail) {
                  setEmailDialogOpen(false);
                  setEmailError('');
                }
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export const PipelineReport: React.FunctionComponent<Props> = (props) => {
  const { isDashboard = false, showEmailButton = true } = props;
  const [rows, setRows] = useState<PipelineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    if (isDashboard) {
      authFetchJson(`${API_BASE_URL}/dashboard/open-opportunities`)
        .then(({ json }) => {
          setRows(json);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error loading dashboard data:', error);
          setLoading(false);
        });
    } else {
      authFetchJson(`${API_BASE_URL}/reports/pipeline`)
        .then(({ json }) => {
          setRows(json);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load pipeline report', err);
          setLoading(false);
        });
    }
  }, [isDashboard]);

  const exportToCSV = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/reports/pipeline/csv`);
      if (!response.ok) {
        throw new Error(`Failed to download CSV (${response.status})`);
      }

      const blob = await response.blob();
      const filename = getFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
        `pipeline-report-${new Date().toISOString().slice(0, 10)}.csv`
      );
      triggerDownload(blob, filename);
    } catch (error) {
      notify(`Error: ${getErrorMessage(error)}`, { type: 'error' });
    }
  };

  const exportToPDF = async () => {
    setExportingPdf(true);

    try {
      const response = await authFetch(`${API_BASE_URL}/reports/pipeline/pdf`);
      if (!response.ok) {
        throw new Error(`Failed to download PDF (${response.status})`);
      }

      const blob = await response.blob();
      const filename = getFilenameFromContentDisposition(
        response.headers.get('Content-Disposition'),
        `pipeline-report-${new Date().toISOString().slice(0, 10)}.pdf`
      );
      triggerDownload(blob, filename);
    } catch (error) {
      notify(`Error: ${getErrorMessage(error)}`, { type: 'error' });
    } finally {
      setExportingPdf(false);
    }
  };

  const columns = useMemo<GridColDef[]>(() => {
    const openedColumn: GridColDef = {
      field: 'dateOpened',
      headerName: isDashboard ? 'Received' : 'Opened',
      width: 160,
      renderCell: (params) => formatReportDate(params.row?.dateOpened),
      sortComparator: compareReportDates
    };

    const actionColumn: GridColDef = {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            color="primary"
            component={Link}
            to={`/opportunities/${params.row.id}/show`}
            target="_blank"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            component={Link}
            to={`/opportunities/${params.row.id}`}
            target="_blank"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    };

    if (isDashboard) {
      return [
        {
          field: 'contactName',
          headerName: 'Lead',
          width: 200,
          renderCell: (params) => (
            <MuiLink
              component={Link}
              target={'_blank'}
              to={`/contacts/${params.row.contactId}/show`}
            >
              {params.value}
            </MuiLink>
          )
        },
        {
          field: 'companyName',
          headerName: 'Company',
          width: 180,
          renderCell: (params) =>
            params.row.companyId ? (
              <MuiLink
                component={Link}
                target={'_blank'}
                to={`/companies/${params.row.companyId}/show`}
              >
                {params.value}
              </MuiLink>
            ) : (
              params.value || ''
            )
        },
        {
          field: 'stage',
          headerName: 'Stage',
          width: 160
        },
        { field: 'status', headerName: 'Status', width: 140 },
        openedColumn,
        actionColumn
      ];
    }

    return [
      openedColumn,
      { field: 'status', headerName: 'Status', width: 175 },
      {
        field: 'stage',
        headerName: 'Stage',
        width: 210
      },
      {
        field: 'companyName',
        headerName: 'Company',
        width: 220,
        renderCell: (params) =>
          params.row.companyId ? (
            <MuiLink
              component={Link}
              target={'_blank'}
              to={`/companies/${params.row.companyId}/show`}
            >
              {params.value}
            </MuiLink>
          ) : (
            params.value || ''
          )
      },
      {
        field: 'contactName',
        headerName: 'Contact',
        width: 220,
        renderCell: (params) => (
          <MuiLink component={Link} target={'_blank'} to={`/contacts/${params.row.contactId}/show`}>
            {params.value}
          </MuiLink>
        )
      },
      {
        field: 'opportunityName',
        headerName: 'Opportunity',
        flex: 1,
        renderCell: (params) => (
          <MuiLink component={Link} target={'_blank'} to={`/opportunities/${params.row.id}/show`}>
            {params.value}
          </MuiLink>
        )
      }
    ];
  }, [isDashboard]);

  return (
    <>
      <Box>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            {(!isDashboard || showEmailButton) && (
              <Stack direction="row" spacing={1} mb={2} alignItems="center">
                {!isDashboard && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={exportToPDF}
                      disabled={exportingPdf}
                      startIcon={<PictureAsPdfIcon />}
                    >
                      {exportingPdf ? 'Downloading PDF...' : 'Download PDF'}
                    </Button>
                    <Button variant="outlined" onClick={exportToCSV} startIcon={<TableViewIcon />}>
                      Download CSV
                    </Button>
                  </>
                )}
                {showEmailButton && (
                  <PipelineReportEmailButton days={isDashboard ? '15' : undefined} />
                )}
              </Stack>
            )}

            <PipelineGrid rows={rows} columns={columns} isDashboard={isDashboard} />
          </>
        )}
      </Box>
    </>
  );
};
